<template>
  <section class="testKVWebRTC">
    <div id="viewer">
      <h2>Viewer</h2>
      <div class="row">
        <div class="col">
          <v-select
            v-model="selectedCamera"
            :hint="
              selectedCamera
                ? `${selectedCamera.name}, ${selectedCamera.webRTCChannelName}, ${selectedCamera.awsRegionName}, ${selectedCamera.webRTCChannelName}`
                : ''
            "
            :items="cameraList"
            item-text="name"
            item-value="id"
            label="Select Camera"
            persistent-hint
            return-object
            single-line
          ></v-select>
        </div>
      </div>
      <br />
      <div class="testKVWebRTC-actions">
        <span class="send-message datachannel">
          <v-btn class="btn btn-primary"> Send DataChannel Message </v-btn>
        </span>

        <v-btn
          id="start-viewer-button"
          class="btn btn-primary"
          @click="handleStartViewer"
        >
          Start Viewer
        </v-btn>
        <v-btn
          id="stop-viewer-button"
          class="btn btn-primary"
          @click="handleStopViewer"
        >
          Stop Viewer
        </v-btn>
      </div>
      <br />
      <br />
      <div class="row">
        <div class="col testKVWebRTC-local_view">
          <h5>Return Channel</h5>
          <div class="video-container">
            <video
              ref="localView"
              class="local-view"
              autoplay
              playsinline
              controls
              muted
            />
          </div>
        </div>
        <div class="col">
          <h5>
            From Master {{ selectedCamera ? `(${selectedCamera.name})` : '' }} -
            Signaling Client State: {{ signalingClientStateText }} - ICE State:
            {{ iceState }}
          </h5>
          <div class="video-container">
            <video
              ref="remoteView"
              class="remote-view"
              autoplay
              playsinline
              controls
            />
          </div>
        </div>
      </div>
      <div class="row datachannel">
        <div class="col">
          <div class="form-group">
            <textarea
              ref="localMessage"
              type="text"
              class="form-control local-message"
              placeholder="DataChannel Message"
            >
            </textarea>
          </div>
        </div>
        <div class="col">
          <div class="card bg-light mb-3">
            <pre
              ref="remoteMessage"
              class="
                remote-message
                card-body
                text-monospace
                preserve-whitespace
              "
            >
            <div v-for="(message, index) in remoteMessages" :key="index">{{message}}</div>
            </pre>
          </div>
        </div>
      </div>
    </div>

    <h3 id="logs-header">Logs</h3>
    <div class="card bg-light mb-3 testKVWebRTC-logs_wrapper">
      <pre id="logs" class="card-body text-monospace preserve-whitespace">
        <div v-for="(log, index) in logsReverse" :key="index">
          {{log}}
        </div>
      </pre>
    </div>
  </section>
</template>

<script>
import { KVWebRTCService } from '@/services/kinesis/kv-webrtc.service';
import { mapState } from 'vuex';
export default {
  name: 'TestKVWebRTC',
  data() {
    return {
      remoteMessages: [],
      logs: [],
      selectedCamera: null,
      kvWebRTCService: null,
      signalingClientState: 3,
      iceState: 'New',
    };
  },
  computed: {
    ...mapState({
      accessKeyId: (state) => state.common.iam.accessKeyId,
      secretAccessKey: (state) => state.common.iam.secretAccessKey,
      cameraList: (state) => state.common.cameraList,
      networkOnline: (state) => state.common.networkOnline,
    }),
    logsReverse() {
      return this.logs.slice().reverse();
    },
    signalingClientStateText() {
      switch (this.signalingClientState) {
        case 0:
          return 'Connecting';
        case 1:
          return 'Open';
        case 2:
          return 'Closing';
        case 3:
          return 'Closed';
        default:
          return 'Unknown';
      }
    },
  },
  watch: {
    networkOnline(newValue) {
      this.handleNeworkStatusChange(newValue);
    },
  },
  created() {
    this.configureLogging();
  },
  methods: {
    handleNeworkStatusChange(newValue) {
      if (newValue && this.kvWebRTCService) {
        this.handleStartViewer();
      }
    },
    getRandomClientId() {
      return Math.random().toString(36).substring(2).toUpperCase();
    },
    configureLogging() {
      const log = (level, messages) => {
        const text = messages
          .map((message) => {
            if (typeof message === 'object') {
              return JSON.stringify(message, null, 2);
            } else {
              return message;
            }
          })
          .join(' ');
        this.logs.push(`[${new Date().toISOString()}] [${level}] ${text}`);
      };

      console._error = console.error;
      console.error = function (...rest) {
        log('ERROR', Array.prototype.slice.call(rest));
        console._error.apply(this, rest);
      };

      console._warn = console.warn;
      console.warn = function (...rest) {
        log('WARN', Array.prototype.slice.call(rest));
        console._warn.apply(this, rest);
      };

      console._log = console.log;
      console.log = function (...rest) {
        log('INFO', Array.prototype.slice.call(rest));
        console._log.apply(this, rest);
      };
    },
    handleStartViewer() {
      const localView = this.$refs.localView;
      const remoteView = this.$refs.remoteView;
      if (!this.selectedCamera) {
        this.logs.push('No camera selected');
        return;
      }
      if (!this.kvWebRTCService) {
        this.kvWebRTCService = new KVWebRTCService();
      } else {
        this.kvWebRTCService.stopViewer();
      }
      const config = {
        region: this.selectedCamera.awsRegionName,
        channelName: this.selectedCamera.webRTCChannelName,
        clientId: this.getRandomClientId(),
        sendVideo: false,
        sendAudio: false,
        openDataChannel: false,
        widescreen: false,
        fullscreen: false,
        useTrickleICE: true,
        natTraversalDisabled: false,
        forceTURN: false,
        accessKeyId: this.accessKeyId,
        endpoint: null,
        secretAccessKey: this.secretAccessKey,
        sessionToken: null,
      };
      this.kvWebRTCService.startViewer(
        localView,
        remoteView,
        config,
        ({ data }) => {
          this.remoteMessages.push(data);
        },
        (remoteMessage) => {
          console.log('[Remote Message]', remoteMessage);
        },
        (state) => {
          this.signalingClientState = state;
        },
        ({ iceState }) => {
          this.iceState = iceState;
        }
      );
    },
    handleStopViewer() {
      this.iceState = 'Disconnected';
      this.kvWebRTCService.stopViewer();
    },
  },
};
</script>

<style lang="scss">
.testKVWebRTC {
  overflow-y: auto;
  padding: 30px;
  max-width: 890px;
  video {
    max-width: 400px;
  }
  &-local_view {
    visibility: hidden;
    max-width: 0;
    max-height: 0;
    position: absolute;
  }
  &-actions {
    display: flex;
    justify-content: space-around;
  }
  &-logs_wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    word-wrap: break-word;
    background-color: #fff;
    background-clip: border-box;
    border: 1px solid rgba(0, 0, 0, 0.125);
    border-radius: 0.25rem;
    background-color: #f8f9fa !important;
    margin-bottom: 1rem !important;
  }
  #logs {
    height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    flex: 1 1 auto;
    padding: 1.25rem;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace !important;
  }
}
</style>
